import React, { useEffect, useMemo, useState } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  BlockHead,
  Icon,
  Button,
} from "../../components/Component";
import BasePaginationTable from "../../components/table/BasePaginationTable";
import TableTags from "../../components/table/TableTags";
import { defaultParams } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import {
  getMiLibraryList,
  deleteMiLibrarySuccess,
} from "../../slice/mi-library";
import AddMiLibraryModal from "./modals/AddMiLibraryModal";
import DeleteConfirmationModal from "../admin/modals/DeleteConfimationModal";
import { deleteMiLibrary } from "../../api/mi-library";
import { toast } from "react-toastify";

// Category names returned by the API can be either a plain string (admin
// asset list goes through the localized path, legacy data may also be a
// string) or the new { en, ar? } object — handle both safely.
const readCategoryName = (name) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  return name.en ?? name.ar ?? "";
};

const getImageSource = (asset) =>
  asset?.url ||
  asset?.image ||
  asset?.path ||
  asset?.secure_url ||
  asset?.asset ||
  asset?.thumbnail ||
  "";

const getAssetName = (asset) =>
  asset?.name ||
  asset?.fileName ||
  asset?.filename ||
  asset?.publicId ||
  asset?.public_id ||
  asset?.title ||
  asset?._id ||
  "-";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const getDefaultColumns = (onEdit, onDelete) => [
  {
    head: <span className="sub-text">{"MI Library"}</span>,
    cell: (row) => {
      const src = getImageSource(row);
      return src ? (
        <img
          src={src}
          alt="mi-library-preview"
          width={150}
          style={{ objectFit: "contain", borderRadius: "8px", height: "auto" }}
        />
      ) : (
        <span>-</span>
      );
    },
  },
  {
    head: <span className="sub-text">{"Name"}</span>,
    cell: (row) => <span>{getAssetName(row)}</span>,
  },
  {
    head: <span className="sub-text">{"Category"}</span>,
    cell: (row) => <span>{readCategoryName(row?.category?.name) || "-"}</span>,
  },
  {
    head: <span className="sub-text">{"Cost"}</span>,
    cell: (row) => <span>{formatCurrency(row?.cost)}</span>,
  },
  {
    head: <span className="sub-text">{"Tags"}</span>,
    cell: (row) => <TableTags tags={row?.tags} />,
  },
  {
    head: <span className="sub-text"></span>,
    cellClassName: "nk-tb-col-tools",
    cell: (row) => (
      <ul
        className="nk-tb-actions gx-1"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <li>
          <a
            href="#edit"
            className="btn btn-icon btn-trigger"
            onClick={(ev) => {
              ev.preventDefault();
              onEdit(row);
            }}
          >
            <Icon name="edit"></Icon>
          </a>
        </li>
        <li>
          <a
            href="#delete"
            className="btn btn-icon btn-trigger btn-tooltip link-danger"
            onClick={(ev) => {
              ev.preventDefault();
              onDelete(row);
            }}
          >
            <Icon name="trash"></Icon>
          </a>
        </li>
      </ul>
    ),
  },
];

const MiLibraryPage = () => {
  const dispatch = useDispatch();
  const { list, summary, loading } = useSelector((state) => state.miLibrary);
  const [params, setParams] = useState(defaultParams);
  const [smOption, setSmOption] = useState(false);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);

  useEffect(() => {
    dispatch(getMiLibraryList(params));
  }, [dispatch, params]);

  const handlePageChange = (page) => {
    setParams((prev) => ({
      ...prev,
      page,
    }));
  };

  const onDeleteItem = async (item) => {
    if (!item?._id) return;
    try {
      await deleteMiLibrary(item._id);
      dispatch(deleteMiLibrarySuccess(item));
      toast.success("Delete item successfully");
    } catch (error) {
      toast.error(error?.message || "Delete item failed");
    }
    setDeletingItem(null);
  };

  const dataTableColumns = useMemo(
    () => getDefaultColumns(setEditingItem, setDeletingItem),
    []
  );

  return (
    <React.Fragment>
      <Head title="MI Library"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>MI Library</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand me-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="nk-block-tools g-3">
                    <li className="nk-block-tools-opt">
                      <Button
                        className="toggle d-none d-md-inline-flex"
                        color="primary"
                        onClick={() => setAddModalOpen(true)}
                      >
                        <Icon name="plus"></Icon>
                        <span>Add Item</span>
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <BasePaginationTable
          list={list}
          loading={loading}
          columns={dataTableColumns}
          onPageChange={handlePageChange}
          hasNextPage={summary?.hasNextPage}
          totalItems={summary?.count}
          {...params}
        />
      </Content>
      <AddMiLibraryModal
        isOpen={isAddModalOpen || Boolean(editingItem)}
        closeModal={() => {
          setAddModalOpen(false);
          setEditingItem(null);
        }}
        item={editingItem}
      />
      <DeleteConfirmationModal
        onConfirm={() => onDeleteItem(deletingItem)}
        isOpen={Boolean(deletingItem)}
        onCloseModal={() => setDeletingItem(null)}
      />
    </React.Fragment>
  );
};

export default MiLibraryPage;
